import { FormControl, FormErrorMessage } from "@chakra-ui/react";
import { ReactNode, useState } from "react";
import { FloatingFieldContext, FloatingFieldSize } from "./context";
import { sizeStyles } from "./sizeStyles";
import { containerStyles, transparentContainerStyles } from "./containerStyles";
import FloatingLabel from "./Label";
import FloatingText from "./Text";
import FloatingTextArea from "./TextArea";
import FloatingTimePicker from "./TimePicker";
import FloatingTrix from "./Trix";
import FloatingCalendar from "./Calendar";
import FloatingTimezone from "./Timezone";
import FloatingLocation from "./Location";
import FloatingSingleSelect from "./SingleSelect";
import FloatingAsyncMultiSelect from "./AsyncMultiSelect";
import FloatingCreatableSingle from "./CreatableSingle";
import FloatingStaticSelect from "./StaticSelect";
import FloatingUserSelect from "./UserSelect";
import FloatingMultiSelect from "./MultiSelect";

interface FloatingFieldProps {
  name: string;
  size?: FloatingFieldSize;
  errors?: string;
  required?: boolean;
  children: ReactNode;
  /** When true, drop the default tinted FormControl bg — for use inside a ComposeBox
   *  or any host that already owns the surface color. */
  transparent?: boolean;
}

function FloatingField({ name, size = "md", errors, required, children, transparent = false }: FloatingFieldProps) {
  // Start true so pre-filled fields render with a floated label on the first paint.
  // Children call setHasValue(false) via useLayoutEffect when their field is empty;
  // that update is synchronous (before paint) so empty fields never flash as floated.
  const [hasValue, setHasValue] = useState(true);
  const { inputHeight, fontSize } = sizeStyles[size];

  return (
    <FloatingFieldContext.Provider value={{ size, hasValue, setHasValue, name, required }}>
      <FormControl
        isInvalid={!!errors}
        position="relative"
        h={inputHeight}
        bg={transparent ? "transparent" : "blackAlpha.50"}
        borderTopRadius="md"
        fontSize={fontSize}
        _dark={transparent ? undefined : { bg: "whiteAlpha.50" }}
        _focusWithin={{ outline: "none", boxShadow: "none" }}
        sx={(transparent ? transparentContainerStyles : containerStyles)[size]}
      >
        {children}
        {errors && (
          <FormErrorMessage mt={1} fontSize="xs">
            {errors}
          </FormErrorMessage>
        )}
      </FormControl>
    </FloatingFieldContext.Provider>
  );
}

FloatingField.Label = FloatingLabel;
FloatingField.Text = FloatingText;
FloatingField.TextArea = FloatingTextArea;
FloatingField.TimePicker = FloatingTimePicker;
FloatingField.Trix = FloatingTrix;
FloatingField.Calendar = FloatingCalendar;
FloatingField.Timezone = FloatingTimezone;
FloatingField.Location = FloatingLocation;
FloatingField.SingleSelect = FloatingSingleSelect;
FloatingField.AsyncMultiSelect = FloatingAsyncMultiSelect;
FloatingField.CreatableSingle = FloatingCreatableSingle;
FloatingField.StaticSelect = FloatingStaticSelect;
FloatingField.UserSelect = FloatingUserSelect;
FloatingField.MultiSelect = FloatingMultiSelect;

export { FloatingField };
export default FloatingField;
